<template>
    <div class="bg-white border border-border-primary rounded-lg shadow-xl h-[400px] w-[320px] overflow-hidden flex flex-col">
        <div class="p-3 border-b border-border-primary">
            <TextInput
                v-model="searchQuery"
                placeholder="Search portfolios..."
                leading-icon="magnifying-glass"
                autofocus
                class="border-none"
            />
        </div>

        <div class="flex flex-1 overflow-hidden">
            <NavigationDropdownColumn title="Portfolios" class="w-80" highlight>
                <div v-if="displayedPortfolios.length === 0" class="p-4 text-center text-text-secondary paragraph-md">
                    No results found
                </div>
                <NavigationDropdownList v-else>
                    <NavigationDropdownItem
                        v-for="(portfolioItem, index) in displayedPortfolios"
                        :key="portfolioItem.id"
                        :label="portfolioItem.name"
                        :to="portfolioRoutePreserving(portfolioItem)"
                        :current="portfolioItem.id === currentPortfolioId"
                        :selected="index === focusedIndex"
                        @mousemove="focusedIndex = index"
                        @click="closeDropdown"
                    />
                </NavigationDropdownList>
            </NavigationDropdownColumn>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import TextInput from '@/vue/components/design-system/input/TextInput.vue'
import type { BreadcrumbPortfolio } from '../breadcrumb.types'
import { useBreadcrumb_experimental } from '../use-breadcrumbs'
import { useKeyboardNavigation } from '../use-keyboard-navigation'
import { useNavigationRoutes } from '../use-navigation-routes'
import { useNavigationSearch } from '../use-navigation-search'
import NavigationDropdownColumn from './ui/navigation-dropdown-column.vue'
import NavigationDropdownItem from './ui/navigation-dropdown-item.vue'
import NavigationDropdownList from './ui/navigation-dropdown-list.vue'

defineProps<{
    isOpen: boolean
}>()

const emit = defineEmits<{
    'update:isOpen': [value: boolean]
}>()

const router = useRouter()
const { company, portfolio } = useBreadcrumb_experimental()
const { portfolioRoutePreserving } = useNavigationRoutes()

const currentPortfolioId = computed(() => portfolio.value?.id || null)
const companyPortfolios = computed<BreadcrumbPortfolio[]>(() => company.value?.portfolios || [])

const searchQuery = ref('')
const { isSearching, searchResults } = useNavigationSearch(companyPortfolios, searchQuery)

const displayedPortfolios = computed(() => {
    return isSearching.value ? searchResults.value.map(result => result.item) : companyPortfolios.value
})

function closeDropdown() {
    emit('update:isOpen', false)
}

const { focusedIndex } = useKeyboardNavigation({
    items: displayedPortfolios,
    searchQuery,
    onSelect: (selectedPortfolio: BreadcrumbPortfolio) => {
        closeDropdown()
        router.push(portfolioRoutePreserving(selectedPortfolio))
    },
})
</script>
